import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { downloadFile } from '../api/client'
import {
  useAbsences,
  useCopyWeek,
  useCreateAssignment,
  useDaySchedule,
  useDeleteAssignment,
  useNearby,
  useProjects,
  useScheduleRange,
  useTasks,
  useUpdateAssignment,
  useUsers
} from '../api/queries'
import type {
  Absence,
  ScheduleAbsence,
  ScheduleNextAction,
  ScheduleRow,
  ScheduleTask,
  WorkAssignment
} from '../api/types'
import { AddressLink, PhoneLink } from '../components/SiteContact'
import { useAuth } from '../auth/AuthProvider'
import { Avatar, Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { weekdayNames } from '../lib/format'
import { projectOptionLabel } from '../lib/projectLabel'

type View = 'day' | 'week' | 'project'

/** yyyy-MM-dd i lokal tid (ikke UTC, så datoen ikke skifter om aftenen). */
function toIso(date: Date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function fromIso(iso: string) {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function addDays(iso: string, days: number) {
  const date = fromIso(iso)
  date.setDate(date.getDate() + days)
  return toIso(date)
}

/** Mandag i den uge datoen ligger i. */
function startOfWeek(iso: string) {
  const date = fromIso(iso)
  const offset = (date.getDay() + 6) % 7
  date.setDate(date.getDate() - offset)
  return toIso(date)
}

function formatLong(iso: string) {
  const text = fromIso(iso).toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function formatShort(iso: string) {
  return fromIso(iso).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })
}

interface AddTarget {
  userId?: number
  date: string
}

/**
 * Kalender med dagsoversigt: hvilke medarbejdere er på hvilke sager (kravspec: planlægning).
 *
 * Tre visninger af de samme data, fordi der bliver spurgt om tre forskellige ting:
 * - **Dag**: hvem laver hvad i dag? Den skærm en montør åbner om morgenen.
 * - **Uge**: medarbejdere x 7 dage. Her planlægges der, og her kan poster trækkes rundt.
 * - **Sag**: hvem er på sagen, og hvilke dage? Det spørgsmål kunden ringer med.
 *
 * Planlagte poster kommer fra work_assignments; stiplede felter er opgaver og
 * næste handlinger afledt af sagerne - dem kan man ikke flytte, for de har ingen
 * planlagt tid.
 */
export function CalendarPage() {
  const today = toIso(new Date())
  const [date, setDate] = useState(today)
  const [view, setView] = useState<View>('day')
  const [addTarget, setAddTarget] = useState<AddTarget | null>(null)
  const [editing, setEditing] = useState<WorkAssignment | null>(null)
  const [copying, setCopying] = useState(false)
  const [printError, setPrintError] = useState<string | null>(null)
  const [printing, setPrinting] = useState(false)
  const { isProjectManager, user } = useAuth()

  const weekStart = startOfWeek(date)
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart])

  function shift(days: number) {
    setDate((current) => addDays(current, days))
  }

  const canPlanFor = (userId: number) => isProjectManager || userId === user?.id

  async function printDay() {
    setPrintError(null)
    setPrinting(true)
    try {
      await downloadFile(`/api/schedule/day/pdf?date=${date}`, `Dagsplan ${date}.pdf`)
    } catch (error) {
      setPrintError(error instanceof Error ? error.message : 'Dagsplanen kunne ikke hentes.')
    } finally {
      setPrinting(false)
    }
  }

  return (
    <>
      <div className="cal-toolbar">
        <div className="cal-title">
          {view === 'day' ? formatLong(date) : `Uge ${isoWeek(weekStart)} · ${formatShort(weekStart)} - ${formatShort(addDays(weekStart, 6))}`}
        </div>
        <div className="btn-group">
          <button className="btn btn--sm" onClick={() => shift(view === 'day' ? -1 : -7)}>‹</button>
          <button className="btn btn--sm" onClick={() => setDate(today)}>I dag</button>
          <button className="btn btn--sm" onClick={() => shift(view === 'day' ? 1 : 7)}>›</button>
        </div>
        <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
        <div className="btn-group">
          <button className={`btn btn--sm ${view === 'day' ? 'btn--primary' : ''}`} onClick={() => setView('day')}>Dag</button>
          <button className={`btn btn--sm ${view === 'week' ? 'btn--primary' : ''}`} onClick={() => setView('week')}>Uge</button>
          <button className={`btn btn--sm ${view === 'project' ? 'btn--primary' : ''}`} onClick={() => setView('project')}>Sag</button>
        </div>

        {/* Udskriften hører til dagen - en uge kan ikke være på ét stykke papir. */}
        {view === 'day' && (
          <button className="btn btn--sm" disabled={printing} onClick={printDay}>
            {printing ? 'Henter ...' : 'Udskriv dagsplan'}
          </button>
        )}
        {view !== 'day' && (
          <button className="btn btn--sm" onClick={() => setCopying(true)}>
            Kopiér uge
          </button>
        )}

        <button className="btn btn--sm btn--primary" onClick={() => setAddTarget({ date, userId: isProjectManager ? undefined : user?.id })}>
          Planlæg
        </button>
      </div>

      {printError && <div className="error-box" style={{ marginBottom: 14 }}>{printError}</div>}

      {addTarget && <AssignmentForm target={addTarget} onClose={() => setAddTarget(null)} />}
      {editing && <AssignmentForm existing={editing} onClose={() => setEditing(null)} />}
      {copying && <CopyWeekDialog weekStart={weekStart} onClose={() => setCopying(false)} />}

      {view === 'day' && (
        <DayView
          date={date}
          today={today}
          canPlanFor={canPlanFor}
          onAdd={(userId) => setAddTarget({ userId, date })}
          onEdit={setEditing}
        />
      )}
      {view === 'week' && (
        <WeekView
          days={weekDays}
          today={today}
          canPlanFor={canPlanFor}
          onAdd={(userId, day) => setAddTarget({ userId, date: day })}
          onEdit={setEditing}
        />
      )}
      {view === 'project' && <ProjectView days={weekDays} today={today} onEdit={setEditing} />}
    </>
  )
}

function isoWeek(iso: string) {
  const date = fromIso(iso)
  const target = new Date(date.valueOf())
  const dayNumber = (date.getDay() + 6) % 7
  target.setDate(target.getDate() - dayNumber + 3)
  const firstThursday = target.valueOf()
  target.setMonth(0, 1)
  if (target.getDay() !== 4) {
    target.setMonth(0, 1 + ((4 - target.getDay() + 7) % 7))
  }
  return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000)
}

function DayView({
  date,
  today,
  canPlanFor,
  onAdd,
  onEdit
}: {
  date: string
  today: string
  canPlanFor: (userId: number) => boolean
  onAdd: (userId: number) => void
  onEdit: (assignment: WorkAssignment) => void
}) {
  const { data, isLoading, error } = useDaySchedule(date)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data || data.rows.length === 0) return <EmptyState>Ingen aktive medarbejdere.</EmptyState>

  const busy = data.rows.filter((r) => r.assignments.length > 0 || r.tasks.length > 0).length

  return (
    <Card>
      <div className="small muted" style={{ marginBottom: 10 }}>
        {busy} af {data.rows.length} medarbejdere har planlagt arbejde {date === today ? 'i dag' : 'denne dag'}.
        Fuldt optrukne felter er planlagte sager; stiplede er opgaver med deadline eller i gang.
      </div>
      <div className="table-wrap">
        <table className="cal-table">
          <thead>
            <tr>
              <th>Medarbejder</th>
              <th>Sager / opgaver</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.user.id}>
                <td className="cal-user">
                  <div className="row">
                    <Avatar user={row.user} />
                    <div>
                      <div>{row.user.displayName}</div>
                      {/* Nummeret her, så man kan ringe til kollegaen uden først at
                          lede efter ham et andet sted (beslutning 041). */}
                      {row.user.phoneNumber && (
                        <div className="tiny" style={{ fontWeight: 400 }}>
                          <PhoneLink phone={row.user.phoneNumber} />
                        </div>
                      )}
                      <div className="tiny muted" style={{ fontWeight: 400 }}>
                        {row.assignments.length} planlagt · {row.tasks.length} opgaver
                        {row.nextActions.length > 0 && ` · ${row.nextActions.length} næste handling`}
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <RowCell
                    row={row}
                    canPlan={canPlanFor(row.user.id)}
                    onAdd={() => onAdd(row.user.id)}
                    onEdit={onEdit}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function RowCell({
  row,
  canPlan,
  onAdd,
  onEdit
}: {
  row: ScheduleRow
  canPlan: boolean
  onAdd: () => void
  onEdit: (assignment: WorkAssignment) => void
}) {
  return (
    <div className="cal-cell">
      {/* Fravaeret staar foerst: man skal se, at manden ikke er der, foer man
          laeser hvad han skulle lave (beslutning 053). Posterne fjernes ikke -
          en mand kan vaere kaldt ind fra ferie, og saa skal begge dele staa der. */}
      {(row.absences ?? []).map((a) => (
        <AbsenceChip key={a.id} absence={a} />
      ))}
      {row.assignments.map((a) => (
        <AssignmentChip key={a.id} assignment={a} canEdit={canPlan} onEdit={onEdit} />
      ))}
      {row.tasks.map((t) => (
        <TaskChip key={t.taskId} task={t} />
      ))}
      {row.nextActions.map((n) => (
        <NextActionChip key={n.projectId} action={n} />
      ))}
      {row.assignments.length === 0 && row.tasks.length === 0 && row.nextActions.length === 0 &&
        (row.absences ?? []).length === 0 && <span className="cal-empty">Ikke planlagt</span>}
      {canPlan && (
        <button className="cal-add" onClick={onAdd}>
          + Planlæg på sag
        </button>
      )}
    </div>
  )
}

/**
 * Fravær på en række i kalenderen.
 *
 * Farven kommer fra serveren, så ferie ser ens ud alle steder i systemet, og
 * teksten siger hvor længe - ellers ville man skulle regne det ud af to datoer
 * for at vide, om manden er tilbage i morgen.
 */
function AbsenceChip({ absence }: { absence: ScheduleAbsence }) {
  const oneDay = absence.startDate === absence.endDate

  return (
    <span
      className="cal-chip cal-chip--absence"
      style={{ borderColor: absence.color, color: absence.color }}
      title={absence.note ?? undefined}
    >
      <strong>{absence.typeName}</strong>
      {!oneDay && (
        <span className="tiny">
          {' '}
          til {formatShortDate(absence.endDate)}
        </span>
      )}
    </span>
  )
}

/** "12. jan" - kort nok til at stå inde i et mærke. */
function formatShortDate(value: string) {
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })
}

/** Hvad der bæres rundt, mens en post trækkes fra én celle til en anden. */
interface Drag {
  assignment: WorkAssignment
  /** Cellen posten kom fra - så et slip det samme sted ikke bliver til et kald. */
  from: string
}

function WeekView({
  days,
  today,
  canPlanFor,
  onAdd,
  onEdit
}: {
  days: string[]
  today: string
  canPlanFor: (userId: number) => boolean
  onAdd: (userId: number, day: string) => void
  onEdit: (assignment: WorkAssignment) => void
}) {
  const { data: users } = useUsers()
  const { data, isLoading, error } = useScheduleRange(days[0], days[6])
  const { data: absences } = useAbsences(days[0], days[6])
  const move = useUpdateAssignment()

  // Træk-og-slip: flyt en planlagt post til en anden mand eller en anden dag.
  // Der findes ingen HTML5-drag på en telefon - derfor kan det samme gøres i
  // redigeringsdialogen, som blyanten på hver post åbner.
  const [drag, setDrag] = useState<Drag | null>(null)
  const [over, setOver] = useState<string | null>(null)
  const [moveError, setMoveError] = useState<string | null>(null)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  const byUserAndDay = new Map<string, WorkAssignment[]>()
  for (const a of data ?? []) {
    const key = `${a.user.id}:${a.date}`
    byUserAndDay.set(key, [...(byUserAndDay.get(key) ?? []), a])
  }

  // Fravaeret kommer som perioder, men ugen tegnes dag for dag - saa perioden
  // foldes ud paa de dage, den daekker (beslutning 053).
  //
  // Ligger to perioder oven i hinanden - man kan blive syg midt i en ferie -
  // vises den, der begyndte SENEST. Den er den mest praecise forklaring paa,
  // hvorfor manden ikke er der i dag, og begge staar stadig i det, man faar frem
  // ved at holde musen over.
  const absenceByCell = new Map<string, Absence>()
  for (const a of absences ?? []) {
    for (const day of days) {
      if (day < a.startDate || day > a.endDate) continue

      const cell = `${a.user.id}:${day}`
      const shown = absenceByCell.get(cell)
      if (!shown || a.startDate >= shown.startDate) {
        absenceByCell.set(cell, a)
      }
    }
  }

  async function drop(userId: number, day: string) {
    const current = drag
    setDrag(null)
    setOver(null)
    if (!current) return

    const target = `${userId}:${day}`
    if (current.from === target) return

    // Serveren afviser det også, men en besked her er hurtigere end et 403.
    if (!canPlanFor(userId)) {
      setMoveError('Du kan kun flytte din egen planlægning.')
      return
    }

    setMoveError(null)
    const a = current.assignment
    try {
      await move.mutateAsync({
        id: a.id,
        body: {
          userId,
          projectId: a.projectId ?? null,
          deviceId: a.deviceId ?? null,
          taskId: a.taskId ?? null,
          date: day,
          startTime: a.startTime ?? null,
          endTime: a.endTime ?? null,
          note: a.note ?? null
        }
      })
    } catch (e) {
      setMoveError(e instanceof Error ? e.message : 'Posten kunne ikke flyttes.')
    }
  }

  return (
    <Card>
      <div className="small muted" style={{ marginBottom: 10 }}>
        Træk en planlagt post til en anden medarbejder eller en anden dag for at flytte den.
        På telefon og tablet bruges blyanten på posten i stedet.
      </div>
      {moveError && <div className="error-box" style={{ marginBottom: 10 }}>{moveError}</div>}
      <div className="table-wrap">
        <table className="cal-table">
          <thead>
            <tr>
              <th>Medarbejder</th>
              {days.map((day) => (
                <th key={day} className={day === today ? 'cal-today' : ''}>
                  {weekdayNames[fromIso(day).getDay()].slice(0, 3)} {formatShort(day)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users?.map((u) => (
              <tr key={u.id}>
                <td className="cal-user">
                  <div className="row">
                    <Avatar user={u} />
                    {u.displayName}
                  </div>
                </td>
                {days.map((day) => {
                  const cell = `${u.id}:${day}`
                  const items = byUserAndDay.get(cell) ?? []
                  const away = absenceByCell.get(cell)
                  const classes = [
                    day === today ? 'cal-today' : '',
                    over === cell && drag && drag.from !== cell ? 'cal-drop' : '',
                    away ? 'cal-away' : ''
                  ].filter(Boolean).join(' ')

                  return (
                    <td
                      key={day}
                      className={classes}
                      onDragOver={(e) => {
                        if (!drag) return
                        e.preventDefault()
                        setOver(cell)
                      }}
                      onDragLeave={() => setOver((c) => (c === cell ? null : c))}
                      onDrop={(e) => {
                        e.preventDefault()
                        void drop(u.id, day)
                      }}
                    >
                      <div className="cal-cell">
                        {/* Fravaeret staar foerst, saa man ser det, foer man
                            slipper en post paa dagen. Der spaerres ikke: en mand
                            kan kaldes ind fra ferie. */}
                        {away && (
                          <span
                            className="cal-away__label"
                            style={{ color: away.color }}
                            title={away.note ?? `${away.typeName} ${away.startDate} - ${away.endDate}`}
                          >
                            {away.typeName}
                          </span>
                        )}
                        {items.map((a) => (
                          <AssignmentChip
                            key={a.id}
                            assignment={a}
                            canEdit={canPlanFor(u.id)}
                            compact
                            onEdit={onEdit}
                            onDragStart={canPlanFor(u.id) ? () => setDrag({ assignment: a, from: cell }) : undefined}
                            onDragEnd={() => {
                              setDrag(null)
                              setOver(null)
                            }}
                          />
                        ))}
                        {canPlanFor(u.id) && (
                          <button className="cal-add" onClick={() => onAdd(u.id, day)}>
                            +
                          </button>
                        )}
                      </div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

/**
 * Ugen set fra sagen i stedet for fra medarbejderen.
 *
 * Det er den vinkel, en kunde ringer med ("hvornår er I hos os?") og den man
 * skal bruge for at se, om der overhovedet er nogen på sagen i denne uge.
 * Data er de samme som ugevisningens - kun grupperingen er vendt om.
 */
function ProjectView({
  days,
  today,
  onEdit
}: {
  days: string[]
  today: string
  onEdit: (assignment: WorkAssignment) => void
}) {
  const { data, isLoading, error } = useScheduleRange(days[0], days[6])
  const { isProjectManager, user } = useAuth()

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data || data.length === 0) {
    return <EmptyState>Der er ikke planlagt arbejde i denne uge.</EmptyState>
  }

  // Nøglen skelner sag fra enhed: et servicebesøg har ingen sag (beslutning 028).
  const groups = new Map<string, { title: string; customer?: string | null; link: string; days: Map<string, WorkAssignment[]> }>()

  for (const a of data) {
    const key = a.projectId ? `p:${a.projectId}` : `d:${a.deviceId}`
    let group = groups.get(key)
    if (!group) {
      group = {
        title: a.projectId
          ? a.projectOrderNumber ? `${a.projectOrderNumber} · ${a.projectName}` : a.projectName
          : `Service: ${a.deviceName ?? ''}`,
        customer: a.projectCustomer,
        link: a.projectId ? `/projects/${a.projectId}` : `/devices/${a.deviceId}`,
        days: new Map()
      }
      groups.set(key, group)
    }
    group.days.set(a.date, [...(group.days.get(a.date) ?? []), a])
  }

  const sorted = [...groups.values()].sort((a, b) => a.title.localeCompare(b.title, 'da'))

  return (
    <Card>
      <div className="small muted" style={{ marginBottom: 10 }}>
        {sorted.length} {sorted.length === 1 ? 'sag' : 'sager'} har folk på i denne uge.
      </div>
      <div className="table-wrap">
        <table className="cal-table">
          <thead>
            <tr>
              <th>Sag</th>
              {days.map((day) => (
                <th key={day} className={day === today ? 'cal-today' : ''}>
                  {weekdayNames[fromIso(day).getDay()].slice(0, 3)} {formatShort(day)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((group) => (
              <tr key={group.link}>
                <td className="cal-user">
                  <Link to={group.link}>{group.title}</Link>
                  {group.customer && <div className="tiny muted" style={{ fontWeight: 400 }}>{group.customer}</div>}
                </td>
                {days.map((day) => {
                  const items = group.days.get(day) ?? []
                  return (
                    <td key={day} className={day === today ? 'cal-today' : ''}>
                      <div className="cal-cell">
                        {items.map((a) => (
                          <PersonChip
                            key={a.id}
                            assignment={a}
                            canEdit={isProjectManager || a.user.id === user?.id}
                            onEdit={onEdit}
                          />
                        ))}
                        {items.length === 0 && <span className="cal-empty">–</span>}
                      </div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

/** En post set fra sagen: her er det manden, der er hovedsagen - ikke sagens navn. */
function PersonChip({
  assignment,
  canEdit,
  onEdit
}: {
  assignment: WorkAssignment
  canEdit: boolean
  onEdit: (assignment: WorkAssignment) => void
}) {
  const time = assignment.startTime
    ? `${assignment.startTime}${assignment.endTime ? `-${assignment.endTime}` : ''}`
    : 'Hele dagen'

  return (
    <div className="cal-chip" style={{ borderLeftColor: assignment.projectStatusColor }}>
      <span className="cal-chip__title row" style={{ gap: 6 }}>
        <Avatar user={assignment.user} size={20} />
        {assignment.user.displayName}
      </span>
      <span className="cal-chip__meta">{time}</span>
      {assignment.taskTitle && <span className="cal-chip__meta">{assignment.taskTitle}</span>}
      {canEdit && (
        <span className="cal-chip__tools">
          <button className="cal-chip__edit" title="Ret planlægning" onClick={() => onEdit(assignment)}>
            ✎
          </button>
        </span>
      )}
    </div>
  )
}

function AssignmentChip({
  assignment,
  canEdit,
  compact = false,
  onEdit,
  onDragStart,
  onDragEnd
}: {
  assignment: WorkAssignment
  canEdit: boolean
  compact?: boolean
  onEdit: (assignment: WorkAssignment) => void
  onDragStart?: () => void
  onDragEnd?: () => void
}) {
  const remove = useDeleteAssignment()
  const time = assignment.startTime ? `${assignment.startTime}${assignment.endTime ? `-${assignment.endTime}` : ''}` : 'Hele dagen'
  const draggable = onDragStart !== undefined

  return (
    <div
      className={`cal-chip ${draggable ? 'cal-chip--draggable' : ''}`}
      style={{ borderLeftColor: assignment.projectStatusColor }}
      draggable={draggable}
      onDragStart={(e) => {
        // Uden nyttelast afviser Firefox trækket helt.
        e.dataTransfer.setData('text/plain', String(assignment.id))
        e.dataTransfer.effectAllowed = 'move'
        onDragStart?.()
      }}
      onDragEnd={onDragEnd}
    >
      <Link
        to={assignment.deviceId ? `/devices/${assignment.deviceId}` : `/projects/${assignment.projectId}`}
        className="cal-chip__title"
        // Et link starter sit eget træk af URL'en og ville stjæle posten fra cellen.
        draggable={false}
      >
        {assignment.deviceId && <span className="cal-chip__service">Service</span>}
        {/* Ordrenummeret foran sagens navn (Simon, 2026-09-24). */}
        {!assignment.deviceId && assignment.projectOrderNumber && (
          <span className="cal-chip__order">{assignment.projectOrderNumber}</span>
        )}
        {assignment.deviceId ? assignment.deviceName : assignment.projectName}
      </Link>
      {assignment.taskTitle && <span className="cal-chip__meta">{assignment.taskTitle}</span>}
      {!compact && assignment.projectCustomer && <span className="cal-chip__meta">{assignment.projectCustomer}</span>}

      {/* I dagsoversigten skal man kunne ringe og finde vej med ét tryk - det er
          den skærm, folk har åben, når de skal ud af døren. */}
      {!compact && (assignment.site?.phone || assignment.site?.address) && (
        <span className="cal-chip__meta cal-chip__contact">
          <PhoneLink phone={assignment.site.phone} />
          {assignment.site.phone && assignment.site.address && <span aria-hidden> · </span>}
          {assignment.site.address && <AddressLink contact={assignment.site} />}
        </span>
      )}
      <span className="cal-chip__meta">
        {time}
        {assignment.note ? ` · ${assignment.note}` : ''}
      </span>
      {canEdit && (
        <span className="cal-chip__tools">
          <button className="cal-chip__edit" title="Ret planlægning" onClick={() => onEdit(assignment)}>
            ✎
          </button>
          <button
            className="cal-chip__remove"
            title="Fjern planlægning"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(`Fjern ${assignment.user.displayName} fra "${assignment.projectName}" ${formatShort(assignment.date)}?`)) {
                remove.mutate(assignment.id)
              }
            }}
          >
            ×
          </button>
        </span>
      )}
    </div>
  )
}

function TaskChip({ task }: { task: ScheduleTask }) {
  const label = task.isOverdue ? 'Forsinket' : task.isDueToday ? 'Deadline i dag' : task.statusName
  return (
    <div className="cal-chip cal-chip--task" style={{ borderLeftColor: task.isOverdue ? '#DC2626' : task.statusColor }}>
      <Link to={`/projects/${task.projectId}`} className="cal-chip__title">
        {task.title}
      </Link>
      <span className="cal-chip__meta">{task.projectName}</span>
      <span className={`cal-chip__meta ${task.isOverdue ? 'overdue' : ''}`}>{label}</span>
    </div>
  )
}

/**
 * Sagens næste handling på den dag, fristen falder (beslutning 040).
 *
 * Den skiller sig ud fra en opgave med en stiplet kant: det er ikke noget, der
 * er planlagt tid til, men noget der skal ske på sagen den dag.
 */
function NextActionChip({ action }: { action: ScheduleNextAction }) {
  const label = action.isOverdue ? 'Overskredet' : 'Frist i dag'

  return (
    <div
      className="cal-chip cal-chip--task"
      style={{
        borderLeftColor: action.isOverdue ? '#DC2626' : action.statusColor,
        borderStyle: 'dashed'
      }}
    >
      <Link to={`/projects/${action.projectId}`} className="cal-chip__title">
        {action.text}
      </Link>
      <span className="cal-chip__meta">
        Næste handling · {action.projectName}
        {action.projectCustomer ? ` · ${action.projectCustomer}` : ''}
      </span>
      <span className={`cal-chip__meta ${action.isOverdue ? 'overdue' : ''}`}>{label}</span>
    </div>
  )
}

/**
 * Kopiér en uges planlægning over på en anden.
 *
 * Den ugentlige planlægning ligner ofte sidste uge: de samme mand på de samme
 * sager. Serveren springer poster over, der allerede står i målugen, så en
 * kopiering kørt to gange ikke fordobler ugen - og den siger hvor mange.
 */
function CopyWeekDialog({ weekStart, onClose }: { weekStart: string; onClose: () => void }) {
  const { isProjectManager, user } = useAuth()
  const { data: users } = useUsers()
  const copy = useCopyWeek()

  const [fromWeek, setFromWeek] = useState(weekStart)
  const [toWeek, setToWeek] = useState(addDays(weekStart, 7))
  const [userId, setUserId] = useState(isProjectManager ? '' : String(user?.id ?? ''))
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setResult(null)

    try {
      const r = await copy.mutateAsync({
        fromWeek,
        toWeek,
        userId: userId ? Number(userId) : null
      })

      if (r.copied === 0 && r.skippedDuplicates === 0 && r.skippedArchived === 0) {
        setResult('Der var ikke noget planlagt i den uge, du kopierede fra.')
        return
      }

      const parts = [
        r.copied === 1 ? 'Kopierede 1 post' : `Kopierede ${r.copied} poster`,
        `til uge ${isoWeek(startOfWeek(r.toWeek))}.`
      ]
      if (r.skippedDuplicates > 0) {
        parts.push(
          r.skippedDuplicates === 1
            ? '1 stod der allerede og blev sprunget over.'
            : `${r.skippedDuplicates} stod der allerede og blev sprunget over.`
        )
      }
      if (r.skippedArchived > 0) {
        parts.push(
          r.skippedArchived === 1
            ? '1 hørte til en arkiveret sag og kom ikke med.'
            : `${r.skippedArchived} hørte til arkiverede sager og kom ikke med.`
        )
      }
      setResult(parts.join(' '))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ugen kunne ikke kopieres.')
    }
  }

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-label="Kopiér uge"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal__card">
        <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>Kopiér uge</h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          Hele ugens planlægning flyttes med: samme mand, samme sag, samme ugedag og klokkeslæt.
          Poster der allerede står i målugen, laves ikke om til dubletter.
        </p>
        <form onSubmit={submit}>
          <div className="form-grid">
            <label className="field">
              <span className="field__label">Fra ugen der indeholder</span>
              <input type="date" required value={fromWeek} onChange={(e) => setFromWeek(e.target.value)} />
              <span className="tiny muted">Uge {isoWeek(startOfWeek(fromWeek))}</span>
            </label>
            <label className="field">
              <span className="field__label">Til ugen der indeholder</span>
              <input type="date" required value={toWeek} onChange={(e) => setToWeek(e.target.value)} />
              <span className="tiny muted">Uge {isoWeek(startOfWeek(toWeek))}</span>
            </label>
            <label className="field" style={{ gridColumn: '1 / -1' }}>
              <span className="field__label">Medarbejder</span>
              <select value={userId} onChange={(e) => setUserId(e.target.value)} disabled={!isProjectManager}>
                {isProjectManager && <option value="">Alle medarbejdere</option>}
                {users?.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName}
                  </option>
                ))}
              </select>
              {!isProjectManager && <span className="tiny muted">Du kan kun kopiere din egen planlægning.</span>}
            </label>
          </div>

          {error && <div className="error-box" style={{ marginBottom: 10 }}>{error}</div>}
          {result && <div className="small" style={{ marginBottom: 10 }}>{result}</div>}

          <div className="btn-group">
            <button
              className="btn btn--primary"
              type="submit"
              disabled={copy.isPending || startOfWeek(fromWeek) === startOfWeek(toWeek)}
            >
              {copy.isPending ? 'Kopierer ...' : 'Kopiér ugen'}
            </button>
            <button className="btn" type="button" onClick={onClose}>
              {result ? 'Luk' : 'Fortryd'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/**
 * Formularen bag både "Planlæg" og blyanten på en post.
 *
 * Ved oprettelse kan flere medarbejdere sættes på i én handling. Ved redigering
 * er der kun én post at rette, så feltet bliver en almindelig liste - og et
 * servicebesøg beholder sin enhed, for en post er enten en sag eller et besøg
 * (beslutning 028) og skifter ikke natur, fordi man flytter den en dag.
 */
/**
 * "Du er der alligevel" - åbne sager tæt på der, hvor medarbejderen skal hen den dag.
 *
 * Udgangspunktet er hans første planlagte stop. Har han intet planlagt endnu,
 * er der intet at regne ud fra, og der vises ingenting - der gættes ikke på,
 * hvor han "plejer" at være.
 *
 * Afstanden er luftlinje og ikke køretid. Det står der, så ingen læser 20 km
 * som tyve minutter - over en fjord uden bro lyver tallet.
 */
function NearbyHint({
  userId,
  date,
  chosenProjectId,
  onChoose
}: {
  userId: number | null
  date: string
  chosenProjectId: number | null
  onChoose: (projectId: number) => void
}) {
  const { data, isLoading } = useNearby(userId, date, 30)

  if (userId === null || isLoading || !data || data.nearby.length === 0) {
    return null
  }

  // Kun sager kan vælges her - et servicebesøg planlægges fra enheden, fordi
  // det skal bindes til enheden og ikke til en sag (beslutning 028).
  const suggestions = data.nearby.filter((n) => n.point.kind === 'project' && n.point.id !== chosenProjectId).slice(0, 5)

  if (suggestions.length === 0) {
    return null
  }

  return (
    <div className="nearby">
      <div className="nearby__head">
        <strong>Han skal alligevel til &quot;{data.originLabel}&quot; den dag.</strong>{' '}
        {suggestions.length === 1 ? 'Der ligger én åben sag' : `Der ligger ${suggestions.length} åbne sager`} i
        nærheden - tryk for at vælge i stedet.
      </div>
      {suggestions.map((n) => (
        <div key={`${n.point.kind}-${n.point.id}`} className="nearby__item">
          <span className="nearby__km">{formatKm(n.distanceKm)}</span>
          <button type="button" className="link-button" onClick={() => onChoose(n.point.id)}>
            {n.point.name}
          </button>
          <span className="tiny muted">{n.point.customerName}</span>
        </div>
      ))}
      <div className="tiny muted" style={{ marginTop: 6 }}>
        Afstanden er i luftlinje, ikke køretid.
      </div>
    </div>
  )
}

/** "4,9 km" - én decimal under 10 km, ellers hele kilometer. */
function formatKm(km: number) {
  return km < 10 ? `${km.toFixed(1).replace('.', ',')} km` : `${Math.round(km)} km`
}

function AssignmentForm({
  target,
  existing,
  onClose
}: {
  target?: AddTarget
  existing?: WorkAssignment
  onClose: () => void
}) {
  const { isProjectManager, user } = useAuth()
  const { data: users } = useUsers()
  const { data: projects } = useProjects({ pageSize: 200 })
  const create = useCreateAssignment()
  const update = useUpdateAssignment()

  const isEdit = existing !== undefined
  const isService = existing?.deviceId != null

  // Flere medarbejdere ad gangen: skal to mand på samme sag samme dag, er det
  // én handling, ikke to. En medarbejder kan kun vælge sig selv.
  const [userIds, setUserIds] = useState<number[]>(() => {
    const start = existing?.user.id ?? target?.userId ?? user?.id
    return start ? [start] : []
  })
  const [projectId, setProjectId] = useState(existing?.projectId ? String(existing.projectId) : '')
  const [taskId, setTaskId] = useState(existing?.taskId ? String(existing.taskId) : '')
  const [date, setDate] = useState(existing?.date ?? target?.date ?? toIso(new Date()))
  const [untilDate, setUntilDate] = useState('')
  const [startTime, setStartTime] = useState(existing?.startTime ?? '')
  const [endTime, setEndTime] = useState(existing?.endTime ?? '')
  const [note, setNote] = useState(existing?.note ?? '')
  const [error, setError] = useState<string | null>(null)

  const { data: tasks } = useTasks(projectId ? { projectId: Number(projectId), pageSize: 200 } : {})

  const pending = create.isPending || update.isPending

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)

    if (isEdit && existing) {
      try {
        await update.mutateAsync({
          id: existing.id,
          body: {
            userId: userIds[0],
            projectId: isService ? null : Number(projectId),
            deviceId: isService ? existing.deviceId : null,
            taskId: taskId ? Number(taskId) : null,
            date,
            startTime: startTime || null,
            endTime: endTime || null,
            note: note || null
          }
        })
        onClose()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Planlægningen kunne ikke gemmes.')
      }
      return
    }

    const additionalDates: string[] = []
    if (untilDate && untilDate > date) {
      let cursor = addDays(date, 1)
      while (cursor <= untilDate && additionalDates.length < 60) {
        const dow = fromIso(cursor).getDay()
        if (dow !== 0 && dow !== 6) additionalDates.push(cursor)
        cursor = addDays(cursor, 1)
      }
    }

    // Ét kald pr. medarbejder. Fejler én - f.eks. fordi vedkommende allerede står
    // på sagen den dag - skal de øvrige stadig gemmes, og man skal kunne se hvem
    // der drillede.
    const results = await Promise.allSettled(
      userIds.map((id) =>
        create.mutateAsync({
          userId: id,
          projectId: Number(projectId),
          taskId: taskId ? Number(taskId) : null,
          date,
          startTime: startTime || null,
          endTime: endTime || null,
          note: note || null,
          additionalDates
        })
      )
    )

    const failed = results
      .map((r, i) => ({ r, navn: users?.find((u) => u.id === userIds[i])?.displayName ?? 'Ukendt' }))
      .filter((x) => x.r.status === 'rejected')

    if (failed.length === 0) {
      onClose()
      return
    }

    setError(
      failed.length === userIds.length
        ? failed[0].r.status === 'rejected' && failed[0].r.reason instanceof Error
          ? failed[0].r.reason.message
          : 'Kunne ikke gemme planlægningen.'
        : `Gemt for ${userIds.length - failed.length}. Kunne ikke gemme for: ${failed.map((f) => f.navn).join(', ')}.`
    )
  }

  function toggleUser(id: number) {
    setUserIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]))
  }

  return (
    // Dialog frem for et kort i toppen: klikker man "Planlæg på sag" nede ved en
    // medarbejder, lå formularen før over skærmkanten, og det lignede at der ikke
    // skete noget.
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? 'Ret planlægning' : 'Planlæg på sag'}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal__card modal__card--wide">
        <h2 style={{ margin: '0 0 14px', fontSize: 18 }}>{isEdit ? 'Ret planlægning' : 'Planlæg på sag'}</h2>
        <form onSubmit={submit}>
          <div className="form-grid">
            {isEdit ? (
              <label className="field">
                <span className="field__label">Medarbejder *</span>
                <select
                  required
                  value={userIds[0] ?? ''}
                  disabled={!isProjectManager}
                  onChange={(e) => setUserIds([Number(e.target.value)])}
                >
                  {users?.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.displayName}
                    </option>
                  ))}
                </select>
                {!isProjectManager && <span className="tiny muted">Du kan kun planlægge dig selv.</span>}
              </label>
            ) : (
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <span className="field__label">
                  Medarbejdere * {userIds.length > 0 && <span className="tiny muted">({userIds.length} valgt)</span>}
                </span>
                <div className="cal-people">
                  {users?.map((u) => {
                    // En medarbejder må kun planlægge sig selv.
                    const allowed = isProjectManager || u.id === user?.id
                    return (
                      <label key={u.id} className={`cal-person ${userIds.includes(u.id) ? 'cal-person--on' : ''}`}>
                        <input
                          type="checkbox"
                          checked={userIds.includes(u.id)}
                          disabled={!allowed}
                          onChange={() => toggleUser(u.id)}
                        />
                        <Avatar user={u} size={22} />
                        <span>{u.displayName}</span>
                      </label>
                    )
                  })}
                </div>
                <span className="tiny muted">Sæt flueben ved alle, der skal på sagen den dag.</span>
              </div>
            )}

            {isService ? (
              <div className="field">
                <span className="field__label">Servicebesøg</span>
                <div style={{ paddingTop: 6 }}>
                  <Link to={`/devices/${existing?.deviceId}`}>{existing?.deviceName}</Link>
                </div>
                <span className="tiny muted">Et servicebesøg kan flyttes, men ikke laves om til en sag.</span>
              </div>
            ) : (
              <label className="field">
                <span className="field__label">Sag / projekt *</span>
                <select required value={projectId} onChange={(e) => { setProjectId(e.target.value); setTaskId('') }}>
                  <option value="">Vælg ...</option>
                  {projects?.items.map((p) => (
                    <option key={p.id} value={p.id}>
                      {projectOptionLabel(p)}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {!isService && (
              <label className="field">
                <span className="field__label">Opgave (valgfri)</span>
                <select value={taskId} onChange={(e) => setTaskId(e.target.value)} disabled={!projectId}>
                  <option value="">Hele sagen</option>
                  {tasks?.items.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.title}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="field">
              <span className="field__label">Dato *</span>
              <input type="date" required value={date} onChange={(e) => setDate(e.target.value)} />
            </label>

            {/* Er manden alligevel i området den dag, skal man kunne se, hvad der
                ellers ligger derude - inden bilen kører. */}
            {!isService && (
              <NearbyHint
                userId={userIds.length === 1 ? userIds[0] : null}
                date={date}
                chosenProjectId={projectId ? Number(projectId) : null}
                onChoose={(id) => {
                  setProjectId(String(id))
                  setTaskId('')
                }}
              />
            )}
            {!isEdit && (
              <label className="field">
                <span className="field__label">Gentag hverdage til og med</span>
                <input type="date" value={untilDate} min={date} onChange={(e) => setUntilDate(e.target.value)} />
              </label>
            )}
            <label className="field">
              <span className="field__label">Fra kl.</span>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Til kl.</span>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Note</span>
              <input value={note} placeholder="F.eks. medbring stige" onChange={(e) => setNote(e.target.value)} />
            </label>
          </div>

          {error && <div className="error-box" style={{ marginBottom: 10 }}>{error}</div>}

          <div className="btn-group">
            <button
              className="btn btn--primary"
              type="submit"
              disabled={pending || userIds.length === 0 || (!isService && !projectId)}
            >
              {pending
                ? 'Gemmer ...'
                : isEdit
                  ? 'Gem ændringen'
                  : userIds.length > 1
                    ? `Planlæg ${userIds.length} medarbejdere`
                    : 'Gem planlægning'}
            </button>
            <button className="btn" type="button" onClick={onClose}>
              Fortryd
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
